import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Autocomplete, Box, TextField } from '@mui/material';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';

import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import {
    formatMonthParam,
    getCurrentMonthStart,
    getPreviousDayMonthStart,
    parseMonthParam
} from '../../utils/dateUtils';
import { empresas } from '../../utils/empresas';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatDecimal, formatPercentage } from '../../utils/numberUtils';
import '../css/realEstimatedBI.css';

const allOption = 'all';

const kpiFilterOptions = [
    allOption,
    { id: 'good', label: 'Acima de 100%' },
    { id: 'warning', label: 'Entre 95% e 100%' },
    { id: 'bad', label: 'Abaixo de 95%' }
];

const minMonth = new Date(2025, 0, 1);

const getOptionLabel = (option, allLabel, labelField = 'label') => {
    if (option === allOption) {
        return allLabel;
    }

    return option[labelField];
};

const hasKpi = (kpi) => kpi !== null && kpi !== undefined;

const getKpiStatus = (kpi) => {
    if (!hasKpi(kpi)) {
        return 'is-nodata';
    }

    const kpiPercent = Number(kpi) * 100;

    if (kpiPercent < 95) {
        return 'is-bad';
    }

    if (kpiPercent < 100) {
        return 'is-warning';
    }

    return 'is-good';
};

const matchesKpiFilter = (kpi, filter) => {
    if (filter === allOption) {
        return true;
    }

    const kpiPercent = Number(kpi) * 100;

    if (filter.id === 'good') {
        return kpiPercent >= 100;
    }

    if (filter.id === 'warning') {
        return kpiPercent >= 95 && kpiPercent < 100;
    }

    return hasKpi(kpi) && kpiPercent < 95;
};

const getFailureStatus = ({ falhas, kpi }) => {
    const hasRegisteredFailures = falhas > 0;
    const isLowKpi = Number(kpi) * 100 < 95;

    if (isLowKpi && !hasRegisteredFailures) {
        return {
            className: 'is-alert',
            icon: <ReportProblemRoundedIcon />,
            label: 'Alerta'
        };
    }

    if (hasRegisteredFailures) {
        return {
            className: 'is-warning',
            icon: <AssignmentTurnedInRoundedIcon />,
            label: 'Falhas'
        };
    }

    return {
        className: 'is-good',
        icon: <CheckCircleRoundedIcon />,
        label: 'OK'
    };
};

const mapKpis = (kpis) => kpis.map((item) => ({
    id: item.inversor_id,
    usinaId: item.usina_id,
    usina: item.usina,
    inversorId: item.inversor_id,
    inversor: item.inversor,
    serial: item.serial,
    potencia: item.potencia,
    falhas: item.falhas,
    kpi: item.kpi
}));

const RealEstimadoInversorBI = () => {
    const [searchParams] = useSearchParams();
    const hasDrillThroughDates = searchParams.has('data_inicio') && searchParams.has('data_fim');
    const initialEmpresa = useMemo(() => {
        const empresaId = searchParams.get('empresa_id');

        if (!empresaId) {
            return allOption;
        }

        return empresas.find((empresa) => String(empresa.id) === empresaId) || allOption;
    }, [searchParams]);
    const initialDataInicio = useMemo(() => hasDrillThroughDates ? parseMonthParam(searchParams.get('data_inicio')) : getCurrentMonthStart(), [hasDrillThroughDates, searchParams]);
    const initialDataFim = useMemo(() => hasDrillThroughDates ? parseMonthParam(searchParams.get('data_fim')) : getCurrentMonthStart(), [hasDrillThroughDates, searchParams]);
    const [sites, setSites] = useState([]);
    const [selectedEmpresa, setSelectedEmpresa] = useState(initialEmpresa);
    const [selectedSite, setSelectedSite] = useState(allOption);
    const [selectedKpiFilter, setSelectedKpiFilter] = useState(allOption);
    const [dataInicio, setDataInicio] = useState(initialDataInicio);
    const [dataFim, setDataFim] = useState(initialDataFim);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const initialFetchStarted = useRef(false);

    const empresaOptions = useMemo(() => [allOption, ...empresas], []);
    const siteOptions = useMemo(() => [allOption, ...sites], [sites]);
    const filteredRows = useMemo(() => {
        return rows.filter((item) => matchesKpiFilter(item.kpi, selectedKpiFilter));
    }, [rows, selectedKpiFilter]);

    const buscarRealEstimadoInversor = useCallback(async ({
        dataInicioValue,
        dataFimValue,
        empresaValue,
        siteValue
    }) => {
        setIsLoading(true);

        const body = JSON.stringify({
            data_inicio: formatMonthParam(dataInicioValue),
            data_fim: formatMonthParam(dataFimValue),
            empresa_id: empresaValue === allOption ? '' : empresaValue.id,
            site_id: siteValue === allOption ? '' : siteValue.key
        });

        const response = await fetchFlask('buscarRealEstimadoInversor', body);
        setRows(mapKpis(response.kpis));
        setIsLoading(false);
    }, []);

    useEffect(() => {
        async function fetchInitialData () {
            if (initialFetchStarted.current) {
                return;
            }

            initialFetchStarted.current = true;

            const response = await fetchFlask('getSites');
            const sitesArray = response.sites.map((obj) => {
                const key = Object.keys(obj)[0];
                const value = obj[key];
                return { key, value };
            });
            const initialSite = sitesArray.find((site) => String(site.key) === searchParams.get('site_id')) || allOption;

            setSites(sitesArray);
            setSelectedSite(initialSite);

            await buscarRealEstimadoInversor({
                dataInicioValue: dataInicio,
                dataFimValue: dataFim,
                empresaValue: initialEmpresa,
                siteValue: initialSite
            });

            setInitialDataLoaded(true);
        }

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!initialDataLoaded) return;

        buscarRealEstimadoInversor({
            dataInicioValue: dataInicio,
            dataFimValue: dataFim,
            empresaValue: selectedEmpresa,
            siteValue: selectedSite
        });
    }, [dataFim, dataInicio, selectedEmpresa, selectedSite]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    const kpiDistribution = useMemo(() => {
        const totalRows = rows.length;
        const goodRows = rows.filter((item) => Number(item.kpi) * 100 >= 100).length;
        const warningRows = rows.filter((item) => {
            const kpiPercent = Number(item.kpi) * 100;
            return kpiPercent >= 95 && kpiPercent < 100;
        }).length;
        const badRows = rows.filter((item) => hasKpi(item.kpi) && Number(item.kpi) * 100 < 95).length;
        const getPercentage = (value) => totalRows ? (value / totalRows) * 100 : 0;

        return {
            totalRows,
            good: getPercentage(goodRows),
            warning: getPercentage(warningRows),
            bad: getPercentage(badRows)
        };
    }, [rows]);

    const columns = useMemo(() => [
        {
            field: 'inversorId',
            headerName: 'ID',
            type: 'number',
            width: 76,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'usina',
            headerName: 'Usina',
            flex: 1,
            minWidth: 220,
            renderCell: (params) => (
                <span className='real-estimated-bi-plant'>
                    <SolarPowerRoundedIcon />
                    {params.value}
                </span>
            )
        },
        {
            field: 'inversor',
            headerName: 'Inversor',
            flex: 1,
            minWidth: 220,
            renderCell: (params) => (
                <span className='real-estimated-bi-inverter'>
                    <MemoryRoundedIcon />
                    {params.value}
                </span>
            )
        },
        {
            field: 'serial',
            headerName: 'Serial',
            flex: 1,
            minWidth: 180
        },
        {
            field: 'potencia',
            headerName: 'Potência (kWp)',
            type: 'number',
            width: 170,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => formatDecimal(params.value)
        },
        {
            field: 'kpi',
            headerName: 'KPI %',
            type: 'number',
            width: 132,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box className='real-estimated-bi-kpi-cell'>
                    <span className={`real-estimated-bi-kpi ${getKpiStatus(params.value)}`}>
                        {hasKpi(params.value) ? `${formatDecimal(Number(params.value) * 100)}%` : 'Sem dado'}
                    </span>
                </Box>
            )
        },
        {
            field: 'falhas',
            headerName: 'Status',
            width: 128,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => {
                const status = getFailureStatus({
                    falhas: params.value,
                    kpi: params.row.kpi
                });

                return (
                    <Box className='real-estimated-bi-failure-cell'>
                        <span className={`real-estimated-bi-failure-tag ${status.className}`}>
                            {status.icon}
                            {status.label}
                        </span>
                    </Box>
                );
            }
        }
    ], []);

    return (
        <Box className='mainBox real-estimated-bi-page'>
            <PageHeader>KPI Real x Estimado por Inversor</PageHeader>

            <Box className='real-estimated-bi-shell'>
                <Box className='real-estimated-bi-filters'>
                    <MonthRangePicker
                        startDate={dataInicio}
                        endDate={dataFim}
                        minDate={minMonth}
                        maxDate={getPreviousDayMonthStart()}
                        disabled={isLoading}
                        onApply={handleDateRangeApply}
                    />

                    <Autocomplete
                        disableClearable
                        options={empresaOptions}
                        value={selectedEmpresa}
                        onChange={(event, value) => setSelectedEmpresa(value)}
                        getOptionLabel={(option) => getOptionLabel(option, 'Todas', 'empresa')}
                        renderInput={(params) => <TextField {...params} label='SPE' />}
                    />

                    <Autocomplete
                        disableClearable
                        options={siteOptions}
                        value={selectedSite}
                        onChange={(event, value) => setSelectedSite(value)}
                        getOptionLabel={(option) => getOptionLabel(option, 'Todos', 'value')}
                        renderInput={(params) => <TextField {...params} label='Site' />}
                    />

                    <Autocomplete
                        disableClearable
                        options={kpiFilterOptions}
                        value={selectedKpiFilter}
                        onChange={(event, value) => setSelectedKpiFilter(value)}
                        getOptionLabel={(option) => getOptionLabel(option, 'Todos', 'label')}
                        renderInput={(params) => <TextField {...params} label='Faixa KPI' />}
                    />
                </Box>

                <Box className='real-estimated-bi-summary'>
                    <Box className='is-good'>
                        <span><CheckCircleRoundedIcon /> Inversores acima de 100%</span>
                        <strong>{formatPercentage(kpiDistribution.good)}%</strong>
                    </Box>
                    <Box className='is-warning'>
                        <span><WarningAmberRoundedIcon /> Inversores entre 95% e 100%</span>
                        <strong>{formatPercentage(kpiDistribution.warning)}%</strong>
                    </Box>
                    <Box className='is-bad'>
                        <span><ReportProblemRoundedIcon /> Inversores abaixo de 95%</span>
                        <strong>{formatPercentage(kpiDistribution.bad)}%</strong>
                    </Box>
                    <Box>
                        <span><QueryStatsRoundedIcon /> Inversores exibidos</span>
                        <strong>{filteredRows.length}</strong>
                    </Box>
                </Box>

                <Box className='real-estimated-bi-table-panel'>
                    <DataGrid
                        className='real-estimated-bi-grid'
                        rows={filteredRows}
                        columns={columns}
                        loading={isLoading}
                        disableRowSelectionOnClick
                        pageSizeOptions={[100, 150]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 100
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default RealEstimadoInversorBI;
